import { useState } from "react";
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
} from "ra-core";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useCrmIdentity } from "@/hooks/useCrmIdentity";
import type { CrmDataProvider } from "../providers/types";
import type { Sale } from "../types";

export function SalesDeleteButton() {
  const record = useRecordContext<Sale>();
  const { identity } = useCrmIdentity();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!record) return null;

  const currentRole =
    identity?.role || (identity?.administrator ? "admin" : "sales");
  const targetRole = record.role || (record.administrator ? "admin" : "sales");

  // Rule 1: Sales callers cannot delete any user
  if (currentRole === "sales") return null;

  // Rule 2: Cannot delete self
  if (
    identity &&
    (record.id === identity.id ||
      ((identity as any).user_id &&
        (record as any).user_id &&
        (identity as any).user_id === (record as any).user_id))
  ) {
    return null;
  }

  // Rule 3: Admin callers can only delete sales users (not admin or superadmin)
  if (currentRole === "admin" && targetRole !== "sales") {
    return null;
  }

  // Superadmin can delete any other non-self user (backend protects last active superadmin)

  const handleDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      await dataProvider.deleteSalesUser(record.id);
      notify("Utente eliminato con successo", { type: "info" });
      setOpen(false);
      refresh();
    } catch (err: any) {
      const msg = err?.message || "Errore durante l'eliminazione dell'utente";
      setError(msg);
      notify(msg, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fullName =
    `${record.first_name || ""} ${record.last_name || ""}`.trim() ||
    record.email;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
        title="Elimina utente"
        aria-label={`Elimina utente ${fullName}`}
        onClick={(e) => {
          e.stopPropagation();
          setError(null);
          setOpen(true);
        }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>

      <Dialog
        open={open}
        onOpenChange={(val) => {
          if (!loading) setOpen(val);
        }}
      >
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Elimina Utente</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare definitivamente l&apos;utente{" "}
              <span className="font-semibold text-foreground">{fullName}</span>{" "}
              ({record.email})? Questa azione è irreversibile.
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive" className="my-2">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? "Eliminazione..." : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
